import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { formatDate } from "../../../utilities/format";
import { OriginalPost } from "./OriginalPostTable";
import BasicButton from "../../common/components/BasicButton";
import Editor from "../../common/components/Editor";
import { useOmniDraftsQuery, useOmniDeleteOriginalPostMutation } from "../../../lib/apollo/graphql/omni/omni-generated";

interface Props {
  post: OriginalPost;
  onSave?: (post: OriginalPost) => void;
  onDelete?: (postId: string) => void;
  onClose: () => void;
  onRefreshDetail?: () => void; // 상세 데이터 새로고침용
  onSchedulePublish?: (postId: string) => void;
}

const OriginalPostViewMode: React.FC<Props> = React.memo(({ post, onSave, onDelete, onClose, onRefreshDetail, onSchedulePublish }) => {
  const navigate = useNavigate();
  const [deleteOriginalPost, { loading: deleteLoading }] = useOmniDeleteOriginalPostMutation();

  const [formData, setFormData] = useState({
    title: post.title,
    status: post.status,
    tags: post.tags,
    sourceUrl: post.sourceUrl || "",
    memo: post.memo || "",
    content: post.content || "",
  });

  // 해당 원문의 draft를 조회
  const { data: draftsData } = useOmniDraftsQuery({
    variables: {
      input: {
        originalPostId: post.id,
        pagination: { page: 1, limit: 1 },
      }
    }
  });

  // post가 변경될 때 formData 업데이트
  useEffect(() => {
    setFormData({
      title: post.title,
      status: post.status,
      tags: post.tags,
      sourceUrl: post.sourceUrl || "",
      memo: post.memo || "",
      content: post.content || "",
    });
  }, [post]);

  // 디버깅용 로그

  const [tagInput, setTagInput] = useState("");

  const STATUS_CONFIG: Record<string, { color: string; label: string }> = {
    NEW: { color: "bg-blue-100 text-blue-800 border-blue-200", label: "신규" },
    SELECTED: { color: "bg-yellow-100 text-yellow-800 border-yellow-200", label: "발행예정" },
    DRAFT_REQUESTED: { color: "bg-green-100 text-green-800 border-green-200", label: "발행중" },
    ON_HOLD: { color: "bg-gray-100 text-gray-800 border-gray-200", label: "보류" },
    DRAFTED: { color: "bg-emerald-100 text-emerald-800 border-emerald-200", label: "발행완료" },
    DRAFT_REQUEST_FAILED: { color: "bg-red-100 text-red-800 border-red-200", label: "발행실패" },
    DELETED: { color: "bg-red-100 text-red-800 border-red-200", label: "삭제됨" },
    REJECTED: { color: "bg-orange-100 text-orange-800 border-orange-200", label: "거부됨" },
  };

  const isEditable = !["DRAFT_REQUESTED", "DRAFTED"].includes(post.status);

  const handleDelete = async () => {
    if (window.confirm("정말로 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다.")) {
      try {
        const result = await deleteOriginalPost({
          variables: {
            input: {
              originalPostId: post.id,
            }
          }
        });

        if (result.data?.deleteOriginalPost) {
          alert('원문이 성공적으로 삭제되었습니다.');
          onDelete?.(post.id);
          onClose();
        } else {
          throw new Error('Delete operation failed');
        }
      } catch (error) {
        console.error('Failed to delete original post:', error);
        alert('원문 삭제에 실패했습니다. 다시 시도해주세요.');
      }
    }
  };

  const handleSave = () => {
    if (!formData.title.trim()) {
      alert("제목을 입력해주세요.");
      return;
    }
    
    const updatedPost: OriginalPost = {
      ...post,
      title: formData.title.trim(),
      // 발행 실패 상태에서 저장할 때는 상태를 유지
      status: post.status === "DRAFT_REQUEST_FAILED" ? "DRAFT_REQUEST_FAILED" : formData.status,
      tags: formData.tags,
      sourceUrl: formData.sourceUrl,
      memo: formData.memo,
      content: formData.content,
    };
    
    onSave?.(updatedPost);
    onClose();
  };

  const handleAddTag = () => {
    const tag = tagInput.trim();
    if (tag && !formData.tags.includes(tag)) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, tag]
      }));
      setTagInput("");
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  const handleTagInputKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAddTag();
    }
  };



  return (
    <div className="p-6">
      <div className="space-y-4">
        {/* 제목 */}
        <div className="flex">
          <label className="text-sm font-medium text-gray-700" style={{ width: '100px', minWidth: '100px', paddingTop: '8px' }}>
            제목 *
          </label>
          {isEditable ? (
            <div className="flex-1 bg-white rounded-lg border border-gray-300 p-4">
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                className="w-full bg-transparent border-none outline-none text-sm text-gray-700"
                placeholder="제목을 입력하세요"
              />
            </div>
          ) : (
            <p className="text-sm text-gray-700 font-semibold">{post.title}</p>
          )}
        </div>

        {/* 상태 */}
        <div className="flex items-center">
          <label className="text-sm font-medium text-gray-700" style={{ width: '100px', minWidth: '100px' }}>상태</label>
          <span className={`w-24 rounded-full px-3 py-1 text-sm font-semibold border ${STATUS_CONFIG[post.status].color} text-center inline-block`}>
            {STATUS_CONFIG[post.status].label}
          </span>
        </div>

        {/* 작성일시 */}
        <div className="flex items-center">
          <label className="text-sm font-medium text-gray-700" style={{ width: '100px', minWidth: '100px' }}>작성일시</label>
          <p className="text-sm text-gray-600">{formatDate(post.createdAt, "YYYY년 MM월 DD일 HH:mm")}</p>
        </div>

        {/* 수정일시 */}
        <div className="flex items-center">
          <label className="text-sm font-medium text-gray-700" style={{ width: '100px', minWidth: '100px' }}>수정일시</label>
          <p className="text-sm text-gray-600">{formatDate(post.updatedAt, "YYYY년 MM월 DD일 HH:mm")}</p>
        </div>

        {/* 최종 수정자 */}
        <div className="flex items-center">
          <label className="text-sm font-medium text-gray-700" style={{ width: '100px', minWidth: '100px' }}>최종 수정자</label>
          <p className="text-sm text-gray-600">{(post as any).updatedBy || "-"}</p>
        </div>

        {/* 출처 URL */}
        <div className="flex">
          <label className="text-sm font-medium text-gray-700" style={{ width: '100px', minWidth: '100px', paddingTop: '8px' }}>원문출처</label>
          {isEditable ? (
            <div className="flex-1 bg-white rounded-lg border border-gray-300 p-4">
              <input
                type="url"
                value={formData.sourceUrl}
                onChange={(e) => setFormData(prev => ({ ...prev, sourceUrl: e.target.value }))}
                className="w-full bg-transparent border-none outline-none text-sm text-gray-700"
                placeholder="https://example.com"
              />
            </div>
          ) : (
            <p className="text-sm text-gray-600">
              {post.sourceUrl ? (
                <a 
                  href={post.sourceUrl} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-800 underline"
                >
                  {post.sourceUrl}
                </a>
              ) : (
                "설정되지 않음"
              )}
            </p>
          )}
        </div>

        {/* 태그 */}
        <div>
          <div className="flex items-start">
            <label className="text-sm font-medium text-gray-700" style={{ width: '100px', minWidth: '100px', paddingTop: '8px' }}>태그</label>
            <div className="flex-1">
              {isEditable ? (
                <>
                  <div className="flex gap-2 mb-3">
                    <input
                      type="text"
                      value={tagInput}
                      onChange={(e) => setTagInput(e.target.value)}
                      onKeyPress={handleTagInputKeyPress}
                      className="flex-1 rounded border border-gray-300 px-3 py-2 focus:border-blue-500 focus:outline-none"
                      placeholder="태그를 입력하고 Enter 또는 추가 버튼을 누르세요"
                    />
                    <button
                      type="button"
                      onClick={handleAddTag}
                      className="rounded bg-gray-500 px-4 py-2 text-sm text-white hover:bg-gray-600"
                    >
                      추가
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {formData.tags.map((tag, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center rounded-full bg-blue-100 px-3 py-1 text-sm font-medium text-blue-800 border border-blue-200"
                      >
                        {tag}
                        <button
                          type="button"
                          onClick={() => handleRemoveTag(tag)}
                          className="ml-2 text-blue-600 hover:text-blue-800"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                    {formData.tags.length === 0 && (
                      <p className="text-sm text-gray-500">설정된 태그가 없습니다.</p>
                    )}
                  </div>
                </>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {post.tags.map((tag, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center rounded-full bg-blue-100 px-3 py-1 text-sm font-medium text-blue-800 border border-blue-200"
                    >
                      {tag}
                    </span>
                  ))}
                  {post.tags.length === 0 && (
                    <p className="text-sm text-gray-500">설정된 태그가 없습니다.</p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* 메모 */}
        <div className="flex">
          <label className="text-sm font-medium text-gray-700" style={{ width: '100px', minWidth: '100px', paddingTop: '8px' }}>메모</label>
          {isEditable ? (
            <div className="flex-1 bg-white rounded-lg border border-gray-300 p-4">
              <textarea
                value={formData.memo}
                onChange={(e) => setFormData(prev => ({ ...prev, memo: e.target.value }))}
                rows={3}
                className="w-full bg-transparent border-none outline-none resize-none text-sm text-gray-700"
                placeholder="메모를 입력하세요"
              />
            </div>
          ) : (
            <p className="text-sm text-gray-600 whitespace-pre-wrap">{post.memo || "메모가 없습니다."}</p>
          )}
        </div>

        {/* 내용 */}
        <div className="flex items-start">
          <label className="text-sm font-medium text-gray-700" style={{ width: '100px', minWidth: '100px', paddingTop: '8px' }}>내용</label>
          <div className="flex-1">
            {isEditable ? (
              <div 
                style={{ 
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  overflow: 'hidden',
                  height: '400px',
                  boxSizing: 'border-box',
                  display: 'flex',
                  flexDirection: 'column'
                }}
              >
                <div style={{ flex: 1, minHeight: 0 }}>
                  <Editor 
                    value={formData.content}
                    onChange={(value) => setFormData(prev => ({ ...prev, content: value }))}
                    height="100%"
                    mediaFolder="original-post"
                  />
                </div>
              </div>
            ) : (
              <div className="bg-white border border-gray-300 rounded-lg p-6 min-h-[300px]">
                <div 
                  className="prose max-w-none"
                  dangerouslySetInnerHTML={{ __html: post.content || "<p>내용이 없습니다.</p>" }}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 하단 버튼 */}
      <div className="sticky bottom-0 flex justify-between border-t bg-white p-4 -mx-6 -mb-6 mt-8">
        <div className="flex gap-3">
          {post.status === "DRAFTED" && (() => {
            const draft = draftsData?.drafts?.items?.[0];
            if (draft?.draftId) {
              return (
                <BasicButton
                  text="발행 원고 확인"
                  variant="primary"
                  className="bg-blue-500 hover:bg-blue-600"
                  onClick={() => navigate(`/omni/manuscript/${draft.draftId}`)}
                />
              );
            } else {
              return (
                <BasicButton
                  text="원고 삭제됨"
                  variant="primary"
                  className="bg-gray-300 text-gray-500 cursor-not-allowed"
                  disabled={true}
                />
              );
            }
          })()}
          {post.status !== "DRAFTED" && (
            <BasicButton
              text={deleteLoading ? "삭제 중..." : "삭제"}
              variant="error"
              onClick={handleDelete}
              disabled={deleteLoading}
            />
          )}
        </div>
        <div className="flex gap-3">
          {isEditable && (
            <BasicButton
              text="저장"
              variant="outlined"
              className="bg-black text-white hover:bg-gray-800 border-black"
              onClick={handleSave}
            />
          )}
          <BasicButton
            text="닫기"
            variant="outlined"
            onClick={onClose}
          />
        </div>
      </div>
    </div>
  );
});

OriginalPostViewMode.displayName = 'OriginalPostViewMode';

export default OriginalPostViewMode;